import { ColorNordicGrey4, Spacing16, Spacing8 } from 'autogen/design-tokens/tokens';
import type { BasketGiftLineViewModel } from 'autogen/swagger/all';
import Flex from 'components/Layout/components/Flex';
import Card from 'components/generic/card';
import ProductImage from 'components/generic/productImage';
import styled from 'styled-components';
import { Typography } from 'components/generic/typography';
import { hideOnDesktop, hideOnMobile } from 'components/pages/Checkout/components/Delivery/shared/styles';
import { breakpoint, sizes } from 'variables';
import { useTranslation } from 'react-i18next';
import { Free } from 'autogen/translation-keys/trans-website-basket';
import PriceWithDiscount from 'components/generic/productPrice/components/PriceWithDiscount';
import { Button } from 'components/Button';
import { useDeleteGiftFromBasketMutation } from 'autogen/swagger/all/hooks';
import { Delete } from 'autogen/translation-keys/trans-website-shared';
import UAEventTracking from 'modules/tracking/UAEventTracking';
import { GwpValue } from 'autogen/translation-keys/trans-website-product-gwpoffer';
import { format } from 'modules/helpers/strings';
import { memo } from 'react';
import { updateBasket } from '../shared/basket';
import { ProductImageContainer } from 'components/generic/productImage/shared/styles';

/* #region styles */
const StyledCard = styled(Card)`
    border-radius: 0;
`;

const Grid = styled.div`
    display: grid;
    gap: ${Spacing16};
    grid-template-columns: 104px 1fr;

    ${breakpoint.up(sizes.lg)} {
        align-items: start;
        grid-template-columns: 104px 1fr;
    }
`;

const ProductActions = styled(Flex.Column)`
    grid-column: 1/-1;

    ${breakpoint.up(sizes.lg)} {
        grid-column: auto;
    }
`;

const StyledButton = styled(Button)`
    background-color: ${ColorNordicGrey4};
`;

const PriceContainer = styled(Flex.Column)`
    text-align: right;
`;
/* #endregion */

const BasketGiftItem = ({ model }: { model: BasketGiftLineViewModel }) => {
    const { image, productId, discountId, name, isReadOnly, marketingValue, offerText } = model;
    const { t } = useTranslation();

    const { mutateAsync: deleteGiftFromBasket } = useDeleteGiftFromBasketMutation({
        onSuccess: () => {
            updateBasket();
            UAEventTracking({ eventCategory: 'giftLine', eventAction: 'remove', eventLabel: 'gwp' });
        }
    });

    return (
        <StyledCard size={null}>
            <Card.Content>
                <Flex.Column>
                    <Grid>
                        <ProductImageContainer>
                            <ProductImage
                                width={104}
                                image={{ ...image, 'aria-label': name }}
                                transformType="SquareScale"
                            />
                        </ProductImageContainer>

                        {/* mobile price */}
                        <Flex.Column justifyContent="space-between" css={hideOnDesktop}>
                            <Flex.Column gap={Spacing8}>
                                {marketingValue && (
                                    <Typography.Body as="span" size="S" strong>
                                        {format(t(GwpValue))(marketingValue)}
                                    </Typography.Body>
                                )}
                                <Typography.Body as="span">{name}</Typography.Body>
                                <Typography.Body as="span">
                                    <b>{offerText}</b>
                                </Typography.Body>
                            </Flex.Column>

                            <PriceContainer gap={Spacing8} alignItems="end">
                                <PriceWithDiscount size={14} price={t(Free)} priceBefore="" />
                            </PriceContainer>
                        </Flex.Column>

                        <ProductActions justifyContent="space-between">
                            {/* desktop price */}
                            <Flex.Row justifyContent="space-between" css={hideOnMobile}>
                                <Flex.Column gap={Spacing8}>
                                    <Typography.Body as="span">{name}</Typography.Body>
                                    <Typography.Body as="span">
                                        <b>{offerText}</b>
                                    </Typography.Body>
                                </Flex.Column>

                                <PriceContainer gap={Spacing8} alignItems="end">
                                    {marketingValue && (
                                        <Typography.Body as="span" size="S" strong>
                                            {format(t(GwpValue))(marketingValue)}
                                        </Typography.Body>
                                    )}
                                    <PriceWithDiscount size={14} price={t(Free)} priceBefore="" />
                                </PriceContainer>
                            </Flex.Row>

                            {!isReadOnly && (
                                <Flex.Row gap={Spacing8} justifyContent="end">
                                    <StyledButton
                                        variant="secondary"
                                        onClick={() => deleteGiftFromBasket({ params: { productId, discountId } })}
                                    >
                                        {t(Delete)}
                                    </StyledButton>
                                </Flex.Row>
                            )}
                        </ProductActions>
                    </Grid>
                </Flex.Column>
            </Card.Content>
        </StyledCard>
    );
};

export default memo(BasketGiftItem);
